import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Music } from 'lucide-react';

const AudioPlayer = ({ tracks }) => {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const audioRef = useRef(null);
  const hasStartedRef = useRef(false); // Track if we've already started

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // The "Aggressive" Silent Listener
  useEffect(() => {
    const playAudio = async () => {
      // Prevent multiple triggers
      if (hasStartedRef.current) return;

      try {
        if (audioRef.current && tracks.length > 0) {
          await audioRef.current.play();
          setIsPlaying(true);
          hasStartedRef.current = true; // Mark as started so we don't try again
          
          // Clean up ALL listeners immediately
          cleanupListeners();
        }
      } catch (error) {
        console.log("Browser blocked audio on this specific interaction, waiting for next...");
        // Note: We do NOT set hasStartedRef to true here, so the next interaction tries again
      }
    };

    const cleanupListeners = () => {
      document.removeEventListener('click', playAudio);
      document.removeEventListener('keydown', playAudio);
      document.removeEventListener('scroll', playAudio);
      document.removeEventListener('wheel', playAudio);
      document.removeEventListener('touchstart', playAudio);
      document.removeEventListener('touchmove', playAudio);
    };

    // Add listeners for EVERY possible interaction
    document.addEventListener('click', playAudio);
    document.addEventListener('keydown', playAudio);
    document.addEventListener('scroll', playAudio);
    document.addEventListener('wheel', playAudio);
    document.addEventListener('touchstart', playAudio);
    document.addEventListener('touchmove', playAudio);

    // Initial attempt (in case they already interacted)
    playAudio();

    return () => {
      cleanupListeners();
    };
  }, [tracks]);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Playback error:', error);
    }
  };

  const nextTrack = () => {
    setCurrentTrackIndex((prev) => (prev + 1) % tracks.length);
    setIsPlaying(true);
  };

  const previousTrack = () => {
    setCurrentTrackIndex((prev) => (prev - 1 + tracks.length) % tracks.length);
    setIsPlaying(true);
  };

  const handleTrackEnd = () => {
    nextTrack();
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value));
  };

  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(console.error);
    }
  }, [currentTrackIndex]);

  if (!tracks || tracks.length === 0) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={tracks[currentTrackIndex]?.url}
        onEnded={handleTrackEnd}
        loop={false}
      />

      {/* Floating Player */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="fixed bottom-8 right-8 z-50"
        data-testid="audio-player"
      >
        <div className="relative">
          {/* Compact View */}
          <motion.div
            className="bg-surface border-2 border-primary p-4 cursor-pointer hover:bg-primary/20 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid="audio-player-toggle"
          >
            <div className="flex items-center gap-3">
              <Music className="w-6 h-6 text-primary" />
              <div className="font-space-mono text-primary text-sm uppercase">
                {isPlaying ? 'PLAYING' : 'PAUSED'}
              </div>
            </div>
          </motion.div>

          {/* Expanded Controls */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-full right-0 mb-2 bg-surface border-2 border-primary p-6 min-w-[300px]"
                data-testid="audio-player-controls"
              >
                {/* Track Info */}
                <div className="mb-4">
                  <div className="font-space-mono text-primary text-xs uppercase mb-1">
                    NOW PLAYING
                  </div>
                  <div className="font-space-mono text-white text-sm">
                    {tracks[currentTrackIndex]?.name || 'Track ' + (currentTrackIndex + 1)}
                  </div>
                </div>

                {/* Playback Controls */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={previousTrack}
                    className="text-primary hover:text-white transition-colors"
                    disabled={tracks.length <= 1}
                    data-testid="audio-player-prev"
                  >
                    <SkipBack className="w-5 h-5" />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={togglePlay}
                    className="bg-primary text-black p-3 hover:bg-white transition-colors"
                    data-testid="audio-player-play-pause"
                  >
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={nextTrack}
                    className="text-primary hover:text-white transition-colors"
                    disabled={tracks.length <= 1}
                    data-testid="audio-player-next"
                  >
                    <SkipForward className="w-5 h-5" />
                  </motion.button>
                </div>

                {/* Volume Control */}
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleMute}
                    className="text-primary hover:text-white transition-colors"
                    data-testid="audio-player-mute"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </motion.button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="flex-1 h-1 bg-border appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer"
                    data-testid="audio-player-volume"
                  />
                </div>

                {/* Track List */}
                {tracks.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="font-space-mono text-primary text-xs uppercase mb-2">
                      PLAYLIST ({tracks.length} TRACKS)
                    </div>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {tracks.map((track, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setCurrentTrackIndex(index);
                            setIsPlaying(true);
                          }}
                          className={`w-full text-left px-2 py-1 font-space-mono text-xs transition-colors ${
                            index === currentTrackIndex
                              ? 'bg-primary text-black'
                              : 'text-text-secondary hover:text-primary'
                          }`}
                          data-testid={`audio-track-${index}`}
                        >
                          {track.name || `Track ${index + 1}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
};

export default AudioPlayer;
